import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import {
  PlatformSettingDefinitionView,
  PlatformSettingWriteInput,
  PLATFORM_SETTING_RETROACTIVE_CONFIRM_REQUIRED,
  setCompanySettingOverride,
  setPlatformDefault,
} from '../../../api/Platform/settings';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle } from '../../../components/ui/Dialog';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { apiErrorsOf, findApiError } from '../../../utils/requestHandling';

/**
 * 🔴 OWNER DECISION E12 — the editor for ONE setting at ONE level (the platform default, or one
 * tenant's override).
 *
 * <p>Two problems drive every choice in this file, and neither is cosmetic.</p>
 *
 * <p><b>1. An empty field must read as "inherits 30", never as "zero", and never as "no limit".</b>
 * A blank box with no annotation reads as zero to everybody who has ever filled in a form. So the
 * platform value is the placeholder, and a line underneath states the state as you type:
 * "Inheriting the platform default of 30" changes to "Overriding the platform default of 30". The
 * state is stated, not implied. And because a tristate can only be reset if the empty state is
 * reachable, clearing the field is a real, savable action (it sends null) rather than a validation
 * error.</p>
 *
 * <p><b>2. "No limit" is a SEPARATE, EXPLICIT control, not the number zero.</b> CO-114 originally
 * encoded "no cap" as 0, which is one backspace away from a blank field. Here it is a deliberate
 * choice with a visible selected state, and choosing it disables the number field so the two states
 * cannot be half-set.</p>
 *
 * <p><b>The retroactive interlock is NOT a UI nicety.</b> Moving the payroll deduction-cap month
 * backwards re-evaluates already-closed payroll months that HR cannot remediate. The consequence is
 * stated at the point of edit, and the server refuses the write until it is acknowledged — so this
 * dialog's confirmation step is the SECOND line of defence, not the only one.</p>
 *
 * <p>🔴 <b>Every refusal arrives in the CATCH, never as a resolved `result.errors`.</b> The response
 * interceptor in `utils/requestHandling` rejects on every non-2xx, and `ToActionResult` maps
 * `Result.Status` straight onto the HTTP status — so every BadRequest/NotFound/Forbidden this
 * feature produces is a 4xx and lands in the catch. The resolved branch is kept only because a
 * 200-carrying-an-errors-envelope costs nothing to check; the reason itself is read with
 * `apiErrorsOf`, which reads any failure shape and never throws.</p>
 *
 * <p>🔴 <b>The retroactive refusal is rendered from its `details`, not from its description.</b>
 * `Error.Description` is a hardcoded ENGLISH paragraph; the server ships `details`
 * (messageKey/currentValue/proposedValue) precisely so the client can build the sentence in the
 * reader's language. Rendering the description verbatim showed English to Arabic and French
 * operators. The description is the fallback for when details are absent, nothing more.</p>
 */

interface Props {
  open: boolean;
  definition: PlatformSettingDefinitionView | null;
  /** null ⇒ editing the PLATFORM DEFAULT. Otherwise the tenant whose override is being edited. */
  companyId: number | null;
  companyName: string | null;
  /** The raw value at THIS level. null at company level = not configured (inherits). */
  currentValue: string | null;
  /** What this setting resolves to one level up. The placeholder, and the "inheriting" number. */
  inheritedValue: string;
  currentNote: string | null;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * `Input` is an untyped `.jsx` primitive with no `.d.ts`, so TypeScript sees it as taking no props
 * and every usage elsewhere in the app lands in the repo's tsc baseline. Declaring the props this
 * file actually passes keeps it out of that baseline rather than adding to it.
 */
const TextField = Input as unknown as React.FC<{
  label?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  description?: string;
  error?: string;
  placeholder?: string;
  type?: string;
  inputMode?: 'numeric' | 'text';
  min?: string | number;
  maxLength?: number;
  disabled?: boolean;
}>;

/**
 * The structured payload on the retroactive refusal (`PlatformSettingService.RetroactiveMoveDetails`).
 * Read defensively: an older server, or any refusal that predates the payload, has no `details` and
 * must fall back to the description rather than render "undefined" at the operator.
 */
interface RetroactiveMoveDetails {
  messageKey: string;
  currentValue: string;
  proposedValue: string;
}

const retroactiveDetailsOf = (details: unknown): RetroactiveMoveDetails | null => {
  if (!details || typeof details !== 'object') return null;
  const bag = details as Record<string, unknown>;
  const { messageKey, currentValue, proposedValue } = bag;
  if (typeof messageKey !== 'string' || messageKey === '') return null;
  if (typeof currentValue !== 'string' || typeof proposedValue !== 'string') return null;
  return { messageKey, currentValue, proposedValue };
};

const monthOf = (value: string | null | undefined): number => {
  if (!value) return Number.NaN;
  const match = /^(\d{4})-(\d{2})$/.exec(value.trim());
  if (!match) return Number.NaN;
  return Number(match[1]) * 12 + Number(match[2]);
};

const PlatformSettingDialog: React.FC<Props> = ({
  open,
  definition,
  companyId,
  companyName,
  currentValue,
  inheritedValue,
  currentNote,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [unlimited, setUnlimited] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Set when the server refused a backwards move. Holds the sentence BUILT FROM the refusal's
   * `details` in the reader's language — not the server's English `description`, which is only the
   * fallback when a refusal carries no details.
   */
  const [retroPrompt, setRetroPrompt] = useState<string | null>(null);

  const sentinel = definition?.unlimitedSentinel ?? null;
  const isPlatformLevel = companyId === null;
  const isMonth = definition?.kind === 'monthStamp';

  useEffect(() => {
    if (!definition) return;
    const raw = currentValue ?? '';
    const isUnlimited = !!sentinel && raw.toLowerCase() === sentinel.toLowerCase();
    setUnlimited(isUnlimited);
    setValue(isUnlimited ? '' : raw);
    setNote(currentNote ?? '');
    setError(null);
    setRetroPrompt(null);
  }, [definition, currentValue, currentNote, sentinel]);

  /**
   * 🔴 Shown BEFORE saving, not only after the server refuses. A consequence an operator reads only
   * in an error message is a consequence they discover by triggering it.
   *
   * 🔴 CLEARING IS A BACKWARDS MOVE TOO, and it used to be the one case this missed. An empty field
   * is exactly what "reset this tenant to the platform default" looks like, so bailing out on an
   * empty `value` skipped the move with the widest blast radius: dropping an override whose month is
   * LATER than the platform default pulls this tenant's enforcement back onto months that are
   * already closed. The platform default it will fall back to is `inheritedValue` — what the console
   * already resolved one level up — so this needs no refetch.
   *
   * Returns the two months to name in the sentence, or null when the move is forwards / not a month
   * setting. `monthOf` yields NaN for anything unparseable and every comparison against NaN is
   * false, so an unknown month never manufactures a warning.
   */
  const retroactiveMove = useMemo<{ current: string; proposed: string } | null>(() => {
    if (!isMonth || unlimited) return null;
    const trimmed = value.trim();

    if (trimmed === '') {
      // The platform default cannot be cleared at all (the Save button is disabled for it), so the
      // only clear that reaches the server is a company override falling back to the platform value.
      if (isPlatformLevel || !currentValue) return null;
      return monthOf(inheritedValue) < monthOf(currentValue)
        ? { current: currentValue, proposed: inheritedValue }
        : null;
    }

    const current = currentValue ?? inheritedValue;
    return monthOf(trimmed) < monthOf(current) ? { current, proposed: trimmed } : null;
  }, [isMonth, unlimited, value, isPlatformLevel, currentValue, inheritedValue]);

  if (!definition) return null;

  const outgoing = (): string | null => {
    if (unlimited && sentinel) return sentinel;
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  };

  const clearing = outgoing() === null;
  // The platform default is the ONLY source of a default now, so clearing it is not a reset, it is
  // an outage. The server refuses it too; this just keeps the operator from getting that far.
  const clearingPlatform = isPlatformLevel && clearing;

  /**
   * The retroactive refusal, said in the reader's language. The server's `description` is a
   * hardcoded English paragraph; `details` carries the same two months as data for exactly this.
   */
  const retroSentence = (retro: { description?: string; details?: unknown }): string => {
    const details = retroactiveDetailsOf(retro.details);
    if (details) {
      return t(details.messageKey, { current: details.currentValue, proposed: details.proposedValue });
    }
    return retro.description || t('platformConfig.retroactiveGeneric');
  };

  const save = async (acknowledgeRetroactive: boolean) => {
    setSaving(true);
    setError(null);
    try {
      const input: PlatformSettingWriteInput = {
        value: outgoing(),
        note: note.trim() === '' ? null : note.trim(),
        acknowledgeRetroactive,
      };
      const result = isPlatformLevel
        ? await setPlatformDefault(definition.key, input)
        : await setCompanySettingOverride(companyId!, definition.key, input);

      if (result.status === 200 && result.payload) {
        onSaved();
        onClose();
        return;
      }
      // Reached only by a 200 that still carries an errors envelope — a 4xx rejects, see the catch.
      const retro = findApiError(result, PLATFORM_SETTING_RETROACTIVE_CONFIRM_REQUIRED);
      if (retro) {
        setRetroPrompt(retroSentence(retro));
        return;
      }
      setError(apiErrorsOf(result)[0]?.description || t('platformConfig.saveError'));
    } catch (caught) {
      const retro = findApiError(caught, PLATFORM_SETTING_RETROACTIVE_CONFIRM_REQUIRED);
      if (retro) {
        setRetroPrompt(retroSentence(retro));
        return;
      }
      // 🔴 The reason is IN the rejected value. Every refusal this feature produces — the platform
      // default cannot be cleared, the key does not exist, the caller is not the operator — arrives
      // here, so discarding it and showing one generic sentence loses every one of them.
      const first = apiErrorsOf(caught)[0];
      setError(first?.description || t('platformConfig.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const title = isPlatformLevel
    ? t('platformConfig.editPlatformTitle', { setting: t(definition.labelKey) })
    : t('platformConfig.editCompanyTitle', { setting: t(definition.labelKey), company: companyName ?? '' });

  const stateLine = clearing
    ? isPlatformLevel
      ? t('platformConfig.platformCannotClear', { documented: definition.documentedDefault })
      : t('platformConfig.inheriting', { value: inheritedValue })
    : t('platformConfig.overriding', { value: inheritedValue });

  return (
    <Dialog open={open} onOpenChange={next => { if (!next && !saving) onClose(); }}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-5">
          <p className="max-w-[65ch] text-sm text-muted-foreground">{t(definition.descriptionKey)}</p>

          {definition.consequenceKey && (
            <p className="max-w-[65ch] rounded-lg border border-border bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground">
              {t(definition.consequenceKey)}
            </p>
          )}

          {sentinel && (
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-foreground">{t('platformConfig.limitMode')}</span>
              <div className="flex flex-wrap gap-2">
                {[false, true].map(option => {
                  const selected = unlimited === option;
                  return (
                    <button
                      key={String(option)}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => setUnlimited(option)}
                      className={[
                        'rounded-md border px-3 py-2 text-sm transition-colors duration-150',
                        selected
                          ? 'border-primary bg-primary/10 font-medium text-primary'
                          : 'border-border text-muted-foreground hover:border-primary/40',
                      ].join(' ')}
                    >
                      {option ? t('platformConfig.noLimit') : t('platformConfig.numericLimit')}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">{t('platformConfig.noLimitHint')}</p>
            </div>
          )}

          {!unlimited && (
            <TextField
              label={t(definition.labelKey)}
              type={isMonth ? 'month' : 'number'}
              inputMode={isMonth ? 'text' : 'numeric'}
              min={isMonth ? undefined : 1}
              value={value}
              placeholder={inheritedValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
              description={stateLine}
            />
          )}

          {retroactiveMove && (
            <div role="status" className="flex items-start gap-3 rounded-lg border border-warning/40 bg-warning/10 p-3">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warning" aria-hidden="true" />
              <p className="text-xs leading-relaxed text-foreground">
                {t('platformConfig.retroactiveWarning', {
                  current: retroactiveMove.current,
                  proposed: retroactiveMove.proposed,
                })}
              </p>
            </div>
          )}

          <TextField
            label={t('platformConfig.note')}
            value={note}
            maxLength={500}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNote(e.target.value)}
            description={t('platformConfig.noteHint')}
          />

          {retroPrompt && (
            <div role="alert" className="flex flex-col gap-3 rounded-lg border border-destructive/40 bg-destructive/10 p-3">
              <p className="text-sm leading-relaxed text-foreground">{retroPrompt}</p>
              <div>
                <Button variant="destructive" size="sm" onClick={() => save(true)} disabled={saving} loading={saving}>
                  {t('platformConfig.confirmRetroactive')}
                </Button>
              </div>
            </div>
          )}

          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>{t('Cancel')}</Button>
          <Button onClick={() => save(false)} disabled={saving || clearingPlatform} loading={saving}>
            {clearing && !isPlatformLevel ? t('platformConfig.resetToDefault') : t('Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PlatformSettingDialog;
